/**
 * BOUMSPORTS — core behaviour.
 *
 * Loaded on every page as a module (deferred by definition). Everything here
 * is a progressive enhancement: with JS disabled the shop still renders,
 * navigates and checks out via standard form posts.
 */

const { routes, strings, moneyFormat } = window.theme;

/* =================================================================
 * Utilities
 * ================================================================= */

/** Formats cents using the shop's own money format, so currency stays correct. */
export function formatMoney(cents) {
  const amount = (cents / 100).toLocaleString('de-DE', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return moneyFormat.replace(/\{\{\s*amount(_no_decimals|_with_comma_separator)?\s*\}\}/, amount);
}

const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/* =================================================================
 * Cart
 *
 * A single source of truth. Every mutation goes through here and then
 * broadcasts `cart:updated`, so the drawer, the header count and any cart
 * page all resync from the same payload and can never drift apart.
 * ================================================================= */

export const Cart = {
  async add(items) {
    const response = await fetch(`${routes.cartAdd}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: Array.isArray(items) ? items : [items] }),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.description || error.message || 'Cart error');
    }

    await this.refresh();
    return response.json();
  },

  async change(line, quantity) {
    const response = await fetch(`${routes.cartChange}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ line, quantity }),
    });
    if (!response.ok) throw new Error('Cart error');
    const cart = await response.json();
    this.broadcast(cart);
    return cart;
  },

  async get() {
    const response = await fetch(`${routes.cart}.js`, { headers: { Accept: 'application/json' } });
    return response.json();
  },

  async refresh() {
    this.broadcast(await this.get());
  },

  broadcast(cart) {
    document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart } }));
  },
};

/* =================================================================
 * <cart-count> — the bubble on the header cart icon
 * ================================================================= */

class CartCount extends HTMLElement {
  connectedCallback() {
    document.addEventListener('cart:updated', ({ detail }) => this.render(detail.cart.item_count));
  }

  render(count) {
    this.textContent = count > 0 ? count : '';
    this.hidden = count === 0;
  }
}
customElements.define('cart-count', CartCount);

/* =================================================================
 * <cart-drawer>
 * ================================================================= */

class CartDrawer extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector('dialog');
    this.body = this.querySelector('[data-cart-body]');

    this.addEventListener('click', this.onClick);
    document.addEventListener('cart:updated', ({ detail }) => this.rerender(detail.cart));
    document.addEventListener('cart:open', () => this.open());

    // Native <dialog> closes on Escape and on backdrop click for free.
    this.dialog?.addEventListener('close', () => {
      document.documentElement.removeAttribute('data-scroll-lock');
    });
  }

  onClick = (event) => {
    const close = event.target.closest('[data-cart-close]');
    if (close) {
      event.preventDefault();
      this.close();
      return;
    }

    const remove = event.target.closest('[data-cart-remove]');
    if (remove) {
      event.preventDefault();
      this.mutate(Number(remove.dataset.line), 0);
      return;
    }

    const step = event.target.closest('[data-cart-step]');
    if (step) {
      event.preventDefault();
      const line = Number(step.dataset.line);
      const next = Number(step.dataset.quantity) + Number(step.dataset.cartStep);
      this.mutate(line, Math.max(0, next));
    }
  };

  async mutate(line, quantity) {
    this.setAttribute('aria-busy', 'true');
    try {
      await Cart.change(line, quantity);
    } finally {
      this.removeAttribute('aria-busy');
    }
  }

  open() {
    if (!this.dialog) return;
    document.documentElement.setAttribute('data-scroll-lock', '');
    this.dialog.showModal();
  }

  close() {
    this.dialog?.close();
  }

  /**
   * Re-fetches the drawer markup from Liquid rather than rebuilding it in JS.
   * Prices, discounts and gift-card logic then stay in one place — Liquid —
   * instead of being reimplemented (and drifting) here.
   */
  async rerender(cart) {
    const url = `${routes.cart}?section_id=cart-drawer`;
    const response = await fetch(url);
    const html = new DOMParser().parseFromString(await response.text(), 'text/html');
    const fresh = html.querySelector('[data-cart-body]');
    if (fresh && this.body) this.body.replaceWith(fresh);
    this.body = this.querySelector('[data-cart-body]');
    this.dataset.count = cart.item_count;
  }
}
customElements.define('cart-drawer', CartDrawer);

/* =================================================================
 * <product-form> — add to cart without leaving the page
 * ================================================================= */

class ProductForm extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    this.button = this.querySelector('[type="submit"]');
    this.errorTarget = this.querySelector('[data-form-error]');
    this.form?.addEventListener('submit', this.onSubmit);
  }

  onSubmit = async (event) => {
    event.preventDefault();
    if (this.button?.disabled) return;

    const label = this.button?.querySelector('[data-button-label]');
    const original = label?.textContent;

    this.button.disabled = true;
    if (this.errorTarget) this.errorTarget.hidden = true;

    try {
      const formData = new FormData(this.form);
      const items = [{ id: Number(formData.get('id')), quantity: Number(formData.get('quantity') || 1) }];

      // Geschenkservice: die Verpackung als eigener Artikel, mit Verweis auf
      // das verpackte Produkt, damit beim Packen klar ist, was hinein gehört.
      const gift = this.form.querySelector('[data-gift-wrap]:checked');
      if (gift) {
        items.push({
          id: Number(gift.value),
          quantity: 1,
          properties: { Für: document.querySelector('.product__title')?.textContent.trim() || '' },
        });
      }

      await Cart.add(items);

      if (label) label.textContent = strings.added;
      document.dispatchEvent(new CustomEvent('cart:open'));
    } catch (error) {
      if (this.errorTarget) {
        this.errorTarget.textContent = error.message;
        this.errorTarget.hidden = false;
      }
    } finally {
      this.button.disabled = false;
      // Give the "Added" confirmation a beat before reverting.
      if (label && original) setTimeout(() => (label.textContent = original), 1500);
    }
  };
}
customElements.define('product-form', ProductForm);

/* =================================================================
 * <bundle-add> — adds the anchor product and one companion in one click
 *
 * Sits above the buy button on den Set-Seiten. Beides geht in EINEM
 * Cart.add([…]) raus, damit der automatische Set-Rabatt sofort greift.
 *
 * Die Größe kommt vom Größenwähler der Seite, nicht aus dem gerenderten HTML.
 *
 * Das war der Fehler, den es hier zu beheben galt: Die beiden Varianten-IDs
 * standen als data-Attribute im Markup, gesetzt beim Rendern auf die jeweils
 * erste verfügbare Variante. Der Größenwechsel läuft aber rein im Browser — die
 * Zeile wird nie neu gerendert. Wer auf der Bra-Seite M wählte und das Set
 * anklickte, bekam Bra M und Short XS in den Warenkorb.
 *
 * Jetzt liegt neben der Zeile für beide Produkte die vollständige
 * Größen→Varianten-Liste (siehe snippets/bundle-deal-row.liquid). Beim Klick
 * wird die gewählte Größe auf BEIDEN Seiten nachgeschlagen. Passt sie nicht auf
 * beiden, wird nichts hinzugefügt — ein Set in zwei Größen ist kein Set.
 * ================================================================= */

class BundleAdd extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('[data-bundle-add]');
    this.errorTarget = this.querySelector('[data-bundle-error]');
    this.sizeTarget = this.querySelector('[data-bundle-size]');

    this.variants = {
      primary: this.readVariants('primary'),
      companion: this.readVariants('companion'),
    };

    // Alle Größen, die in diesem Set überhaupt vorkommen. Daran wird gleich der
    // gewählte Optionswert erkannt — ohne wissen zu müssen, an welcher Stelle
    // die Größen-Option steht (Short: 1, Bra: 2) oder wie sie heißt.
    this.sizes = new Set(
      [...this.variants.primary, ...this.variants.companion]
        .map((variant) => variant.size)
        .filter(Boolean)
    );

    // Zwei mögliche Quellen für die Größe, in dieser Reihenfolge:
    //
    //  1. Eigene Pillen in der Zeile. Die gibt es nur, wenn die Seite selbst
    //     keine Größe anbietet — auf der Taschenseite etwa, wo die Tasche eine
    //     einzige Variante hat, die Leggings dazu aber drei.
    //  2. Der Größenwähler der Seite. Er steht im Kaufformular, das im Markup
    //     NACH dieser Box kommt — deshalb über den gemeinsamen Container
    //     suchen, nicht über `this`.
    this.ownSizes = this.querySelector('[data-bundle-sizes]');
    this.form = this.ownSizes
      ? null
      : (this.closest('[data-buy-anchor]')?.querySelector('product-form form') ?? null);

    (this.ownSizes ?? this.form)?.addEventListener('change', this.sync);

    this.button?.addEventListener('click', this.onClick);
    this.sync();
  }

  readVariants(role) {
    const node = this.querySelector(`[data-bundle-variants="${role}"]`);
    if (!node) return [];
    try {
      return JSON.parse(node.textContent);
    } catch {
      return [];
    }
  }

  /**
   * Die gerade gewählte Größe, oder null.
   *
   * Bei den eigenen Pillen ist das schlicht der angehakte Knopf. Beim
   * Größenwähler der Seite werden alle Optionsfelder gelesen, versteckte
   * eingeschlossen: einwertige Optionen rendert das Template als hidden input,
   * und die Farbe steht bei manchen Produkten vor der Größe. Erkannt wird die
   * Größe daran, dass ihr Wert in den Varianten des Sets vorkommt — so muss
   * weder Position noch Name der Option bekannt sein.
   */
  selectedSize() {
    if (this.ownSizes) {
      return this.ownSizes.querySelector('input:checked')?.value ?? null;
    }

    if (!this.form || this.sizes.size === 0) return null;

    return (
      [...this.form.querySelectorAll('[name^="option"]')]
        .filter((input) => input.type !== 'radio' || input.checked)
        .map((input) => input.value)
        .find((value) => this.sizes.has(value)) ?? null
    );
  }

  /**
   * Beide Varianten zur gewählten Größe. `error` statt stiller Rückfall auf eine
   * andere Größe: lieber gar nichts in den Warenkorb als das Falsche.
   */
  resolve() {
    const size = this.selectedSize();

    if (!size) {
      // Eigene Pillen ohne Auswahl: noch nichts entschieden. Kein Fehler —
      // der Kunde hat ja nichts falsch gemacht —, aber auch nichts zu tun.
      if (this.ownSizes) return { size: null, pending: true };

      // Sonst: die Seite bietet gar keine Größe an (Produkt mit einer einzigen
      // Variante) → bei dem bleiben, was der Server gerendert hat.
      return {
        size: null,
        items: [Number(this.dataset.primary), Number(this.dataset.companion)].filter(Boolean),
      };
    }

    const items = [];
    for (const role of ['primary', 'companion']) {
      const candidates = this.variants[role];

      // Ein Produkt ohne Größen-Option (die Tasche) nimmt seine einzige
      // Variante — die Größe betrifft nur das Zweitteil.
      const match = candidates.some((variant) => variant.size)
        ? candidates.find((variant) => variant.size === size)
        : candidates.find((variant) => variant.available);

      if (!match || !match.available) {
        return { size, error: `Größe ${size} gibt es im Set gerade nicht.` };
      }
      items.push(match.id);
    }

    return { size, items };
  }

  /** Hält Größenhinweis und Button-Zustand am Größenwähler dran. */
  sync = () => {
    const { size, error, pending } = this.resolve();

    if (this.sizeTarget) {
      this.sizeTarget.textContent = size ? `· Größe ${size}` : '';
      this.sizeTarget.hidden = !size;
    }

    if (this.errorTarget) {
      this.errorTarget.textContent = error ?? '';
      this.errorTarget.hidden = !error;
    }

    if (this.button) this.button.disabled = Boolean(error || pending);
  };

  onClick = async () => {
    if (!this.button || this.button.disabled) return;

    const { items, error } = this.resolve();

    if (error || !items?.length) {
      if (this.errorTarget && error) {
        this.errorTarget.textContent = error;
        this.errorTarget.hidden = false;
      }
      return;
    }

    const label = this.button.querySelector('[data-button-label]');
    const original = label?.textContent;

    this.button.disabled = true;
    if (this.errorTarget) this.errorTarget.hidden = true;

    try {
      await Cart.add(items.map((id) => ({ id, quantity: 1 })));

      if (label) label.textContent = strings.added;
      document.dispatchEvent(new CustomEvent('cart:open'));
    } catch (addError) {
      if (this.errorTarget) {
        this.errorTarget.textContent = addError.message;
        this.errorTarget.hidden = false;
      }
    } finally {
      this.button.disabled = false;
      if (label && original) setTimeout(() => (label.textContent = original), 1500);
    }
  };
}
customElements.define('bundle-add', BundleAdd);

/* =================================================================
 * <announcement-bar>
 *
 * On desktop the CSS lays all four messages out side by side, so there is
 * nothing to rotate — the timer is switched off rather than left running
 * against content the shopper can already see. Below that width there is no
 * room for four, so they take turns.
 * ================================================================= */

class AnnouncementBar extends HTMLElement {
  connectedCallback() {
    this.slides = [...this.querySelectorAll('[data-announcement]')];
    if (this.slides.length < 2 || prefersReducedMotion) return;

    this.index = 0;
    this.interval = Number(this.dataset.interval || 4000);

    // Must match the breakpoint in the section's stylesheet.
    this.sideBySide = window.matchMedia('(min-width: 990px)');
    this.sideBySide.addEventListener('change', this.sync);
    this.sync();

    // Pausing on hover lets people actually finish reading a message.
    this.addEventListener('mouseenter', () => this.stop());
    this.addEventListener('mouseleave', this.sync);
    this.addEventListener('focusin', () => this.stop());
    this.addEventListener('focusout', this.sync);
  }

  disconnectedCallback() {
    this.stop();
    this.sideBySide?.removeEventListener('change', this.sync);
  }

  /** Rotate only where the messages are actually stacked on top of each other. */
  sync = () => {
    if (this.sideBySide.matches) this.stop();
    else this.start();
  };

  start() {
    this.stop();
    this.timer = setInterval(() => this.advance(), this.interval);
  }

  stop() {
    clearInterval(this.timer);
  }

  advance() {
    this.slides[this.index].classList.remove('is-active');
    this.index = (this.index + 1) % this.slides.length;
    this.slides[this.index].classList.add('is-active');
  }
}
customElements.define('announcement-bar', AnnouncementBar);

/* =================================================================
 * <site-header> — mobile drawer, dropdowns, hide-on-scroll
 * ================================================================= */

class SiteHeader extends HTMLElement {
  connectedCallback() {
    this.drawer = this.querySelector('[data-nav-drawer]');
    this.toggle = this.querySelector('[data-nav-toggle]');

    this.toggle?.addEventListener('click', () => this.toggleDrawer());
    this.querySelectorAll('[data-nav-close]').forEach((el) =>
      el.addEventListener('click', () => this.closeDrawer())
    );

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') this.closeDrawer();
    });

    // Mobile submenus: expand in place rather than sliding to a second panel.
    this.querySelectorAll('[data-submenu-toggle]').forEach((button) => {
      button.addEventListener('click', () => {
        const expanded = button.getAttribute('aria-expanded') === 'true';
        button.setAttribute('aria-expanded', String(!expanded));
      });
    });

    this.lastScroll = 0;
    window.addEventListener('scroll', this.onScroll, { passive: true });
  }

  onScroll = () => {
    const y = window.scrollY;
    this.classList.toggle('is-stuck', y > 8);
    // Reveal on scroll up, hide on scroll down — but never while the menu is open.
    if (!this.drawer?.hasAttribute('open')) {
      this.classList.toggle('is-hidden', y > this.lastScroll && y > 200);
    }
    this.lastScroll = y;
  };

  toggleDrawer() {
    this.drawer?.hasAttribute('open') ? this.closeDrawer() : this.openDrawer();
  }

  openDrawer() {
    this.drawer?.setAttribute('open', '');
    this.toggle?.setAttribute('aria-expanded', 'true');
    document.documentElement.setAttribute('data-scroll-lock', '');
  }

  closeDrawer() {
    if (!this.drawer?.hasAttribute('open')) return;
    this.drawer.removeAttribute('open');
    this.toggle?.setAttribute('aria-expanded', 'false');
    document.documentElement.removeAttribute('data-scroll-lock');
  }
}
customElements.define('site-header', SiteHeader);

/* =================================================================
 * Scroll reveal
 *
 * One observer for the whole page. Elements unobserve after revealing, so a
 * long homepage does not keep paying for callbacks it will never use again.
 * ================================================================= */

if (!prefersReducedMotion && 'IntersectionObserver' in window) {
  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-revealed');
        observer.unobserve(entry.target);
      });
    },
    { rootMargin: '0px 0px -10% 0px', threshold: 0.1 }
  );

  const observeAll = (root = document) =>
    root.querySelectorAll('[data-reveal]:not(.is-revealed)').forEach((el) => observer.observe(el));

  observeAll();
  // The theme editor injects sections after load; pick those up too.
  document.addEventListener('shopify:section:load', (event) => observeAll(event.target));
} else {
  document.querySelectorAll('[data-reveal]').forEach((el) => el.classList.add('is-revealed'));
}

/* =================================================================
 * <count-up> — animates a number into view
 * ================================================================= */

class CountUp extends HTMLElement {
  connectedCallback() {
    this.target = parseFloat(this.dataset.target || this.textContent);
    this.decimals = Number(this.dataset.decimals || 0);

    if (prefersReducedMotion || !('IntersectionObserver' in window)) {
      this.render(this.target);
      return;
    }

    this.render(0);
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        this.animate();
        observer.disconnect();
      },
      { threshold: 0.5 }
    );
    observer.observe(this);
  }

  animate() {
    const duration = 1200;
    const start = performance.now();

    const tick = (now) => {
      const progress = Math.min((now - start) / duration, 1);
      // easeOutExpo — fast start, gentle landing.
      const eased = progress === 1 ? 1 : 1 - Math.pow(2, -10 * progress);
      this.render(this.target * eased);
      if (progress < 1) requestAnimationFrame(tick);
    };

    requestAnimationFrame(tick);
  }

  render(value) {
    this.textContent = value.toLocaleString('de-DE', {
      minimumFractionDigits: this.decimals,
      maximumFractionDigits: this.decimals,
    });
  }
}
customElements.define('count-up', CountUp);

/* =================================================================
 * Logo intro animation
 *
 * The ten letters rise in one after another, so the mark writes itself.
 *
 * How often it plays is a theme setting. The default is once per session:
 * a Shopify store is a multi-page app, so "every page load" means the logo
 * re-animates on every single click — a nice touch turns into an obstacle
 * between the shopper and the product.
 * ================================================================= */

const logo = document.querySelector('[data-logo-animate]');
const logoMode = window.theme.logoAnimation || 'session';
const logoPlayed = sessionStorage.getItem('boum:logo-played');

const shouldAnimateLogo =
  logo &&
  !prefersReducedMotion &&
  logoMode !== 'off' &&
  !(logoMode === 'session' && logoPlayed);

if (shouldAnimateLogo) {
  logo.classList.add('is-animating');
  sessionStorage.setItem('boum:logo-played', '1');

  /*
   * Wait for ALL letters, not the first one.
   *
   * `animationend` bubbles up from each individual letter path. Listening for
   * it on the logo therefore fired after letter one finished — and removing the
   * class there cut the remaining nine off mid-flight, so they snapped into
   * place instead of rising. getAnimations() sees every letter, so the class
   * only comes off once the last one has landed.
   */
  const letters = logo.getAnimations ? logo.getAnimations({ subtree: true }) : [];

  if (letters.length > 0) {
    Promise.allSettled(letters.map((a) => a.finished)).then(() => {
      logo.classList.remove('is-animating');
    });
  } else {
    // Kein getAnimations() (sehr alte Browser): grob nach der Gesamtdauer.
    setTimeout(() => logo.classList.remove('is-animating'), 1100);
  }
}
